import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons';
import './DeliveryBackModal.css';

/**
 * Delivery Back Confirmation Modal
 * 
 * Premium styled modal for confirming navigation away from Delivery
 * with unsaved changes. Matches HMS design system with blue gradient
 * header and smooth animations.
 * 
 * @param {Object} props
 * @param {string} props.message - Confirmation message to display
 * @param {Function} props.onConfirm - Callback when user confirms leaving
 * @param {Function} props.onCancel - Callback when user cancels
 * @param {Function} props.onDraft - Optional callback to save as draft before leaving
 */
const DeliveryBackModal = ({ message, onConfirm, onCancel, onDraft, draftLabel }) => {
  // Prevent modal from closing when clicking inside it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Close modal when clicking overlay
  const handleOverlayClick = () => {
    onCancel();
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [onCancel]);

  return (
    <div className="delivery-back-modal-overlay" onClick={handleOverlayClick}>
      <div className="delivery-back-modal" onClick={handleModalClick}>
        {/* Header with HMS Blue Gradient */}
        <div className="delivery-back-modal-header">
          <div className="delivery-back-header-content">
            <h3 className="delivery-back-modal-title">Unsaved Changes</h3>
          </div>
          <button
            className="delivery-back-close-btn"
            onClick={onCancel}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body Content */}
        <div className="delivery-back-modal-body">
          {/* Warning Banner */}
          <div className="delivery-back-warning-banner">
            <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
            <div className="delivery-back-warning-text">
              <strong>Warning</strong>
              <p>You have unsaved changes that will be lost if you leave this page.</p>
            </div>
          </div>

          {/* Confirmation Question */}
          <div className="delivery-back-confirm-message">
            <p className="delivery-back-question">
              {message || 'Are you sure you want to go back?'}
            </p>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="delivery-back-modal-footer">
          <button
            className="delivery-back-btn-secondary"
            onClick={onCancel}
          >
            Stay on Page
          </button>
          {onDraft && (
            <button
              className="delivery-back-btn-draft"
              onClick={onDraft}
            >
              {draftLabel || 'Draft'}
            </button>
          )}
          <button
            className="delivery-back-btn-primary"
            onClick={onConfirm}
          >
            Leave Without Saving
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryBackModal;
